import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckSquare, Users, LogIn, ChevronRight, AlertCircle, Zap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatShiftDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.getTime() === today.getTime()) return 'Today';
  if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatUntil = (minutes) => {
  if (minutes == null) return '';
  if (minutes < 0) {
    const past = Math.abs(minutes);
    if (past < 60) return `Started ${past}m ago`;
    return `Started ${Math.floor(past / 60)}h ago`;
  }
  if (minutes < 60) return `Starts in ${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return `Starts in ${h}h ${m}m`;
  return `Starts in ${Math.floor(h / 24)}d`;
};

const roleAccent = {
  superadmin: 'text-rose-300',
  executive: 'text-amber-300',
  admin: 'text-cyan-300',
  manager: 'text-violet-300',
  staff: 'text-slate-300',
  trainee: 'text-emerald-300',
};

export const TodayGlanceWidget = ({ getAuthHeaders, currentUser, onNavigate }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);

  const go = (target) => {
    if (onNavigate) return onNavigate(target);
    const routes = {
      shift: '/staff/leave-management',
      leave: '/staff/leave-management',
      tasks: '/staff/staff-hub',
      directory: '/staff/staff-directory',
    };
    navigate(routes[target] || '/staff/staff-hub');
  };

  const fetchGlance = useCallback(async () => {
    try {
      const h = getAuthHeaders();
      if (!h) return;
      const res = await axios.get(`${BACKEND_URL}/api/staff/today-glance`, { headers: h });
      setData(res.data);
    } catch (e) {
      console.error('today-glance fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchGlance();
    const t = setInterval(fetchGlance, 60000); // refresh every 60s
    return () => clearInterval(t);
  }, [fetchGlance]);

  const handleClockIn = async () => {
    setClocking(true);
    try {
      await axios.post(`${BACKEND_URL}/api/clock/in`, {}, { headers: getAuthHeaders() });
      toast.success('Clocked in — have a great shift!');
      fetchGlance();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClocking(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="today-glance-loading"
        className="rounded-2xl border border-violet-500/10 bg-[#0a0a14]/60 backdrop-blur-md p-6 flex items-center gap-3 text-slate-400"
      >
        <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
        <span className="text-sm">Loading today at a glance…</span>
      </div>
    );
  }

  if (!data) return null;

  const { next_shift, open_tasks, team_online } = data;
  const firstName = (currentUser?.display_name || currentUser?.username || 'there').split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 5 ? 'Still up' : hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : hour < 22 ? 'Good evening' : 'Good night';

  return (
    <div
      data-testid="today-glance-widget"
      className="relative overflow-hidden rounded-2xl border border-violet-500/20 bg-gradient-to-br from-[#0a0a14] via-[#0d0b1f] to-[#0a0a14] p-5 md:p-6"
    >
      {/* subtle grain / glow */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-violet-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-cyan-500/10 blur-3xl" />

      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-violet-300/70">Today at a glance</p>
            <h2 className="text-lg md:text-xl font-semibold text-white mt-0.5">
              {greeting}, <span className="bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent">{firstName}</span>
            </h2>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-slate-400 text-xs">
              {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          {/* Tile 1: Next Shift */}
          <button
            type="button"
            onClick={() => go('shift')}
            data-testid="glance-tile-shift"
            className="group text-left rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] hover:bg-cyan-500/[0.08] hover:border-cyan-500/40 transition-all p-4 flex flex-col gap-2"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-cyan-300" />
                </div>
                <span className="text-[11px] uppercase tracking-wider text-cyan-300/80 font-medium">Next Shift</span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-300 group-hover:translate-x-0.5 transition-all" />
            </div>

            {next_shift ? (
              <>
                <div className="flex items-baseline gap-2">
                  <p className="text-white text-lg font-semibold">{formatShiftDate(next_shift.date)}</p>
                  <p className="text-slate-400 text-sm">{next_shift.start_time} – {next_shift.end_time}</p>
                </div>
                <p className="text-xs text-slate-400 capitalize">
                  {next_shift.shift_type || 'regular'}{next_shift.notes ? ` · ${next_shift.notes}` : ''}
                </p>
                {next_shift.is_clocked_in ? (
                  <span className="mt-1 inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-medium">
                    <Zap className="w-3 h-3" /> You're clocked in
                  </span>
                ) : next_shift.can_clock_in ? (
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid="glance-clock-in-btn"
                    onClick={(e) => { e.stopPropagation(); if (!clocking) handleClockIn(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); if (!clocking) handleClockIn(); } }}
                    className={`mt-1 inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-md bg-cyan-500 hover:bg-cyan-400 text-[#05050A] text-xs font-semibold transition-colors ${clocking ? 'opacity-60 pointer-events-none' : ''}`}
                  >
                    <LogIn className="w-3.5 h-3.5" />
                    {clocking ? 'Clocking in…' : 'Clock In'}
                  </span>
                ) : (
                  <span className="mt-1 text-xs text-slate-500">{formatUntil(next_shift.minutes_until)}</span>
                )}
              </>
            ) : (
              <div className="py-2">
                <p className="text-slate-300 text-sm">No upcoming shift scheduled</p>
                <p className="text-slate-500 text-xs mt-0.5">Enjoy your time off ✨</p>
              </div>
            )}
          </button>

          {/* Tile 2: Open Tasks */}
          <button
            type="button"
            onClick={() => go('tasks')}
            data-testid="glance-tile-tasks"
            className="group text-left rounded-xl border border-violet-500/20 bg-violet-500/[0.04] hover:bg-violet-500/[0.08] hover:border-violet-500/40 transition-all p-4 flex flex-col gap-2"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center">
                  <CheckSquare className="w-4 h-4 text-violet-300" />
                </div>
                <span className="text-[11px] uppercase tracking-wider text-violet-300/80 font-medium">Open Tasks</span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-violet-300 group-hover:translate-x-0.5 transition-all" />
            </div>

            <div className="flex items-baseline gap-2">
              <p className="text-white text-3xl font-semibold leading-none" data-testid="glance-tasks-total">{open_tasks.total}</p>
              <p className="text-slate-400 text-xs">to do</p>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {open_tasks.overdue > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-500/15 border border-rose-500/30 text-rose-300 text-[11px] font-medium">
                  <AlertCircle className="w-3 h-3" /> {open_tasks.overdue} overdue
                </span>
              )}
              {open_tasks.by_priority.high > 0 && (
                <span className="px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[11px] font-medium">
                  {open_tasks.by_priority.high} high
                </span>
              )}
              {open_tasks.by_priority.medium > 0 && (
                <span className="px-2 py-0.5 rounded-md bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-[11px] font-medium">
                  {open_tasks.by_priority.medium} med
                </span>
              )}
            </div>

            {open_tasks.upcoming.length > 0 ? (
              <p className="text-xs text-slate-400 truncate mt-0.5">
                Next: <span className="text-slate-200">{open_tasks.upcoming[0].title}</span>
              </p>
            ) : (
              <p className="text-xs text-slate-500 mt-0.5">You're all caught up 🌿</p>
            )}
          </button>

          {/* Tile 3: Team Online Now */}
          <button
            type="button"
            onClick={() => go('directory')}
            data-testid="glance-tile-team"
            className="group text-left rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] hover:bg-emerald-500/[0.08] hover:border-emerald-500/40 transition-all p-4 flex flex-col gap-2"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center relative">
                  <Users className="w-4 h-4 text-emerald-300" />
                  {team_online.count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  )}
                </div>
                <span className="text-[11px] uppercase tracking-wider text-emerald-300/80 font-medium">Team Online</span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition-all" />
            </div>

            <div className="flex items-baseline gap-2">
              <p className="text-white text-3xl font-semibold leading-none" data-testid="glance-team-count">{team_online.count}</p>
              <p className="text-slate-400 text-xs">clocked in now</p>
            </div>

            {team_online.users.length > 0 ? (
              <div className="flex items-center -space-x-2 mt-1">
                {team_online.users.slice(0, 6).map((u) => (
                  <div
                    key={u.username}
                    title={`${u.display_name}${u.custom_status ? ' · ' + u.custom_status : ''}`}
                    className="w-8 h-8 rounded-full border-2 border-[#0d0b1f] bg-slate-800 overflow-hidden flex items-center justify-center"
                  >
                    {u.avatar_url ? (
                      <img
                        src={u.avatar_url.startsWith('http') ? u.avatar_url : `${BACKEND_URL}${u.avatar_url}`}
                        alt={u.display_name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span className={`text-xs font-semibold ${roleAccent[u.role] || 'text-slate-300'}`}>
                        {(u.display_name || u.username).slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                ))}
                {team_online.count > 6 && (
                  <div className="w-8 h-8 rounded-full border-2 border-[#0d0b1f] bg-slate-800 flex items-center justify-center text-[10px] text-slate-300 font-medium">
                    +{team_online.count - 6}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-500 mt-0.5">No teammates on shift right now</p>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TodayGlanceWidget;
